/**
 * Spec 099 — UI affordance over spec 098's rescan route.
 *
 * Renders the "Rescan against rules" button + confirmation modal in the
 * page header of `/admin/rules/candidates`. Returns null (button NOT in
 * DOM) when the user lacks rule_admin or orbit:admin role — mirrors
 * the per-row Approve/Reject pattern (no disabled-but-visible).
 *
 * On confirm: POSTs the rescan endpoint (spec 098), shows a success
 * toast with the {scanned, moved, no_hit} counts, and calls
 * `onRescanComplete()` so the parent can refetch its candidates list.
 *
 * On error: closes the modal, shows an error toast prefixed
 * "Rescan failed: …", and re-enables the trigger button.
 *
 * `data-testid` attributes wire the .playwright-cli/099-*.mjs gates:
 *   - rescan-button
 *   - rescan-modal
 *   - rescan-modal-title
 *   - rescan-confirm
 *   - rescan-cancel
 *   - rescan-spinner-label  (only present while submitting)
 */
import { useState } from 'react';
import { Icon } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { rescanCandidates } from '../api/ruleIngestion.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';

export interface RescanCandidatesButtonProps {
  /** Called after a successful rescan so the parent can refetch its list. */
  onRescanComplete: () => void;
}

/**
 * Spec 101 — default cosine-similarity threshold. Mirrors
 * `DEFAULT_SIMILARITY_THRESHOLD` in `src/rule-ingestion/dedup.ts` AND
 * `RESCAN_DEFAULT_THRESHOLD` in `NearestRulesPanel.tsx`. Three constants
 * intentionally kept in lockstep — if the team decides to move the default
 * to 0.80, all three change in one commit.
 */
const DEFAULT_THRESHOLD = 0.85;

export function RescanCandidatesButton({ onRescanComplete }: RescanCandidatesButtonProps) {
  const { hasRole } = useAuth();
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Spec 101 FR-101.19 / FR-101.23 — persists across modal opens within page
  // session; hard reload resets to DEFAULT_THRESHOLD.
  const [threshold, setThreshold] = useState<number>(DEFAULT_THRESHOLD);

  // FR-2 — DOM-level role gate. The button (and modal) MUST be absent
  // for non-admins, NOT just disabled. Gate 099.A.2 asserts
  // `page.locator('[data-testid=rescan-button]').count() === 0`.
  const canRescan = hasRole('rule_admin') || hasRole('orbit:admin');
  if (!canRescan) return null;

  function openModal() {
    if (submitting) return;
    setModalOpen(true);
  }

  function cancelModal() {
    // FR-9 — modal cannot be dismissed mid-flight; Cancel button is also
    // disabled below for the same reason.
    if (submitting) return;
    setModalOpen(false);
  }

  async function runRescan() {
    // FR-17 — guard against double-submit when click events overlap.
    if (submitting) return;
    setSubmitting(true);
    try {
      // Spec 101 FR-101.21 — forward the operator-selected threshold to the
      // backend (which re-validates to [0.50, 0.95]).
      const result = await rescanCandidates({ threshold });
      const base = `Scanned ${result.scanned} · Moved ${result.moved_to_suggestions} to suggestions · ${result.no_hit_left_as_candidate} still pending`;
      // Spec 101 FR-101.22 — preserve byte-identical spec 099 toast text when
      // running at the default. Append `· threshold X.XX` only when the
      // operator actually changed it.
      const msg =
        threshold === DEFAULT_THRESHOLD
          ? base
          : `${base} · threshold ${threshold.toFixed(2)}`;
      toast.addToast(msg, 'success');
      setModalOpen(false);
      onRescanComplete();
    } catch (err) {
      // FR-16 — coerce non-string messages defensively. The wrapper
      // already prefixes "Rescan failed: ..." so we render verbatim
      // when present and add the prefix otherwise.
      const raw = err instanceof Error ? err.message : String(err);
      const message = raw.startsWith('Rescan failed:') ? raw : `Rescan failed: ${raw}`;
      toast.addToast(message, 'error');
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button variant="ghost" data-testid="rescan-button" onClick={openModal} disabled={submitting}>
        <Icon name="bx-refresh" className={submitting ? 'animate-spin' : undefined} />
        {submitting ? (
          <span data-testid="rescan-spinner-label">Rescanning…</span>
        ) : (
          'Rescan against rules'
        )}
      </Button>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="rescan-modal-title"
          data-testid="rescan-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <h2
                id="rescan-modal-title"
                data-testid="rescan-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Rescan candidates against rules
              </h2>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                This re-checks every pending candidate against the current rule library.
                Any candidate that is a near-duplicate of an existing rule (cosine
                similarity ≥ the threshold below) will be moved to Rule suggestions for review.
              </p>
              <p>Safe to run repeatedly — already-classified candidates are skipped.</p>

              {/* Spec 101 FR-101.20 — threshold input. Native number input with
                  step 0.05 matches the operator's mental model (0.85 → 0.80 → 0.75).
                  Backend re-validates to [0.50, 0.95] so DevTools/curl bypass is
                  rejected — frontend clamping is UX, not a security boundary. */}
              <label className="block text-sm">
                <span className="font-medium text-slate-700">Threshold</span>
                <input
                  type="number"
                  min="0.50"
                  max="0.95"
                  step="0.05"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  disabled={submitting}
                  data-testid="rescan-threshold-input"
                  className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-50"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Lower threshold = more matches, higher false-positive risk. Default 0.85 is strict; 0.75 often surfaces clear paraphrases.
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="ghost" data-testid="rescan-cancel" onClick={cancelModal} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" data-testid="rescan-confirm" onClick={runRescan} disabled={submitting}>
                {submitting ? 'Rescanning…' : 'Run rescan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
